import React from "react";
import PropTypes from "prop-types";
import styles from "../ModernDashboard.module.css";

const formatCurrency = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

/**
 * Fintech-style balance cards:
 *  - Primary card: gradient background, current balance + status badge
 *  - Secondary card: white card with refundable balance, pending charge, action button
 *  - Skeleton states while loading
 */
export default function BalanceSummary({
  availableBalance,
  refundableBalance,
  pendingPayment,
  status,
  loading,
  onWithdrawalClick,
}) {
  const hasPending = parseFloat(pendingPayment) > 0;

  if (loading) {
    return (
      <div className={styles.mdBalanceGrid}>
        <div className={styles.mdSkeletonCard} />
        <div className={styles.mdSkeletonCard} />
      </div>
    );
  }

  return (
    <div className={styles.mdBalanceGrid}>
      {/* ---- Primary Card: Current Balance ---- */}
      <div className={`${styles.mdBalanceCard} ${styles.mdBalanceCardPrimary}`}>
        <div className={styles.mdCardGlow} />
        <p className={styles.mdCardLabel}>Current Balance</p>
        <h3 className={styles.mdCardAmount}>
          {formatCurrency(availableBalance)}
        </h3>
        <div className={styles.mdStatusBadge}>
          <div
            className={
              status?.toUpperCase() === "ACTIVE"
                ? styles.mdStatusDot
                : styles.mdStatusDotInactive
            }
          />
          {status || "N/A"}
        </div>
      </div>

      {/* ---- Secondary Card: Refundable + Pending ---- */}
      <div className={`${styles.mdBalanceCard} ${styles.mdBalanceCardSecondary}`}>
        <div className={styles.mdMiniRow}>
          <span className={styles.mdMiniLabel}>Refundable Balance</span>
          <span className={styles.mdMiniValue}>
            {formatCurrency(refundableBalance)}
          </span>
        </div>
        <hr className={styles.mdDivider} />
        <div className={styles.mdMiniRow}>
          <span className={styles.mdMiniLabel}>Pending Charge</span>
          <span className={styles.mdMiniValue} style={{ color: hasPending ? "#dc3545" : "#374151" }}>
            {formatCurrency(pendingPayment)}
          </span>
        </div>
        <button
          className={styles.mdActionBtnPrimary}
          onClick={onWithdrawalClick}
        >
          {hasPending ? "Claim Refunds" : "Withdraw Funds"}
        </button>
      </div>
    </div>
  );
}

BalanceSummary.propTypes = {
  availableBalance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  refundableBalance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  pendingPayment: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  status: PropTypes.string,
  loading: PropTypes.bool,
  onWithdrawalClick: PropTypes.func.isRequired,
};

BalanceSummary.defaultProps = {
  loading: false,
};